import api, { type RequestBody } from ".";
import type { PaginatedList } from "src/types/api";
import type { InvoiceDetailData, InvoiceRow } from "src/types/invoice";

/** Query accepted by the invoice list endpoints. */
export interface InvoiceQuery {
	page?: number;
	limit?: number;
	search?: string;
	store_or_warehouse_uuid?: string;
}

/** A member of an invoice group — customers and sub accounts are shaped the same here. */
export interface GroupMember {
	id: number | string;
	first_name?: string;
	last_name?: string;
}

/** Add/remove takes the ids under a key that depends on the tenant type. */
export interface GroupMemberMutation {
	uuid: string;
	data: Partial<Record<"customers" | "subaccounts", (number | string)[]>>;
}

export interface InvoiceGroup {
	uuid?: string;
	/** Numeric key. `group_id` on an invoice is this, not the uuid. */
	id?: number | string;
	name?: string;
	description?: string;
}

export const getInvoices = (params?: InvoiceQuery) => api.get<PaginatedList<InvoiceRow>>("/invoices", { params });

export const getArchivedInvoices = (params?: InvoiceQuery) => api.get<PaginatedList<InvoiceRow>>("/invoices/archived", { params });

export const archiveInvoice = ({ uuid }: { uuid: string }) => api.patch(`/invoices/${uuid}/archive`);

export const markInvoiceAsPaid = ({ uuid }: { uuid: string }) => api.put(`/invoices/${uuid}/mark-as-paid`);

export const unArchiveInvoice = ({ uuid }: { uuid: string }) => api.patch(`/invoices/${uuid}/unarchive`);

export const createInvoice = (data: RequestBody) => api.post<{ uuid?: string }>("/invoices/store", data);

export const getInvoiceDetails = (uuid: string) => api.get<InvoiceDetailData>(`/invoices/${uuid}/view`);

export const sendInvoiceReminder = ({ uuid }: { uuid: string }) => api.post(`/invoices/${uuid}/send-reminder`);

export const getInvoiceGroups = (params?: InvoiceQuery) => api.get<PaginatedList<InvoiceGroup & { uuid: string }>>(`/groups`, { params });

export const createInvoiceGroup = (data: RequestBody) => api.post(`/groups`, data);

export const updateInvoiceGroup = ({ uuid, ...data }: { uuid?: string } & RequestBody) => api.put(`/groups/${uuid}`, data);

export const deleteInvoiceGroup = ({ uuid }: { uuid: string }) => api.delete(`/groups/${uuid}`);

export const getInvoiceGroupMembers = ({ uuid }: { uuid: string }) => api.get<PaginatedList<GroupMember>>(`/groups/${uuid}/members`);

export const removeInvoiceGroupMembers = ({ uuid, data }: GroupMemberMutation) => api.post(`/groups/${uuid}/remove-subaccount`, data);

export const addInvoiceGroupMembers = ({ uuid, data }: GroupMemberMutation) => api.post(`/groups/${uuid}/add-subaccount`, data);
